import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Smartphone, RefreshCcw, ShieldCheck, Check, LogOut, Monitor, Trash2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

/**
 * /settings/devices — Slice K · Device Sign-In QR.
 *
 * Two modes on the same screen:
 *   1. Signed-in device  → mint a 5-min pair code, show it as a QR
 *   2. Unauthenticated (or ?code= in URL) → paste-code form that consumes the QR
 */
export default function DevicePair() {
  const { user, checkAuth } = useAuth();
  const [params]  = useSearchParams();
  const paramCode = params.get("code");
  const paramPair = params.get("pair");

  // If the URL carries ?code= / ?pair= we always render the consume flow —
  // even when the user is already signed in (they may want to move to a fresh
  // account).
  const forceConsume = !!paramCode;

  useSeo({ title: "Sign in on another device — Find2Near" });

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white" data-testid="device-pair-page">
      <div className="flex items-center gap-3 px-4 pt-5 pb-4 border-b border-stone-100">
        <Link to="/account" className="p-2 -ml-2 rounded-full hover:bg-stone-100" data-testid="pair-back">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="heading font-black text-2xl text-stone-900">
          {forceConsume ? "Sign in with QR" : "Sign in on another device"}
        </h1>
      </div>
      <div className="p-4">
        {forceConsume || !user
          ? <ConsumePane paramCode={paramCode} paramPair={paramPair} onSuccess={checkAuth} />
          : <>
              <GeneratePane />
              <DevicesList />
            </>
        }
      </div>
    </div>
  );
}


// ─── Generate side (existing device shows a QR) ──────────────────────────────
function GeneratePane() {
  const [data, setData]       = useState(null);
  const [loading, setLoad]    = useState(false);
  const [remaining, setLeft]  = useState(0);
  const timerRef              = useRef(null);

  const mint = async () => {
    setLoad(true);
    try {
      const r = await api.post("/auth/pair/begin");
      setData(r.data);
      setLeft(r.data.ttl_s || 300);
    } catch {
      toast.error("Couldn't create a pair code — please sign in first");
    } finally {
      setLoad(false);
    }
  };
  useEffect(() => { mint(); }, []);
  useEffect(() => {
    if (!data) return;
    timerRef.current && clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setLeft(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [data]);

  const cancel = async () => {
    try { await api.post("/auth/pair/cancel"); } catch { /* ignore */ }
    setData(null);
  };

  const origin  = typeof window !== "undefined" ? window.location.origin : "";
  const qrUrl   = data ? `${origin}/pair?pair=${data.pair_id}&code=${data.code}` : "";
  const expired = data && remaining <= 0;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 flex gap-3">
        <Smartphone size={22} className="text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          On the new device, open Find2Near and scan this QR. The code auto-expires in 5 minutes.
        </div>
      </div>

      {loading && <div className="text-center text-stone-400 py-8">Preparing…</div>}

      {data && (
        <div className="rounded-2xl border border-stone-200 p-6 text-center">
          <div className="flex justify-center">
            <div className={`p-3 bg-white border border-stone-200 rounded-xl ${expired ? "opacity-30" : ""}`}>
              <QRCodeCanvas value={qrUrl} size={200} includeMargin={false} data-testid="pair-qr" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">6-digit code</div>
            <div
              className={`text-4xl font-black tracking-widest tabular-nums ${expired ? "text-stone-400 line-through" : "text-stone-900"}`}
              data-testid="pair-code"
            >
              {data.code}
            </div>
            <div className="text-xs text-stone-500 mt-2">
              {expired
                ? <span className="text-rose-600 font-bold">Code expired — generate a new one.</span>
                : <>Expires in <b>{Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}</b></>}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button onClick={mint} className="rounded-full border border-stone-300 text-sm font-bold py-2 inline-flex items-center justify-center gap-1" data-testid="pair-regen">
              <RefreshCcw size={13} /> Regenerate
            </button>
            <button onClick={cancel} className="rounded-full border border-rose-300 text-rose-700 text-sm font-bold py-2" data-testid="pair-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Consume side (new device pastes the code / follows the QR) ─────────────
function ConsumePane({ paramCode, paramPair, onSuccess }) {
  const navigate            = useNavigate();
  const [code, setCode]     = useState(paramCode || "");
  const [pair, setPair]     = useState(paramPair || "");
  const [busy, setBusy]     = useState(false);
  const [done, setDone]     = useState(false);
  const attempted           = useRef(false);

  const consume = async (pairId, codeStr) => {
    setBusy(true);
    try {
      await api.post("/auth/pair/consume", { pair_id: pairId, code: codeStr });
      setDone(true);
      toast.success("Signed in on this device");
      onSuccess && onSuccess();
      setTimeout(() => navigate("/"), 1200);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't sign in with that code";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Auto-run when both ?pair and ?code are present in the URL.
  useEffect(() => {
    if (attempted.current) return;
    if (paramCode && paramPair) {
      attempted.current = true;
      consume(paramPair, paramCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramCode, paramPair]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 flex gap-3">
        <ShieldCheck size={22} className="text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          Paste the 6-digit code shown on your other device to sign in here — no password needed.
        </div>
      </div>

      {done ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="pair-done">
          <Check size={28} className="mx-auto text-emerald-600" />
          <div className="mt-2 font-bold text-emerald-900">Signed in</div>
          <div className="text-xs text-emerald-800 mt-1">Redirecting…</div>
        </div>
      ) : (
        <form
          onSubmit={e => { e.preventDefault(); consume(pair.trim(), code.trim()); }}
          className="rounded-2xl border border-stone-200 p-4 space-y-3"
        >
          <input
            type="text"
            placeholder="pair_… (from the QR)"
            value={pair}
            onChange={e => setPair(e.target.value)}
            className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm font-mono"
            data-testid="pair-input-id"
          />
          <input
            type="text"
            placeholder="6-digit code"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="w-full border border-stone-300 rounded-xl px-3 py-3 text-center text-2xl font-black tracking-widest"
            data-testid="pair-input-code"
          />
          <button
            type="submit"
            disabled={busy || code.length < 6 || pair.length < 8}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white font-bold rounded-full py-2.5"
            data-testid="pair-submit"
          >
            {busy ? "Signing in…" : "Sign in on this device"}
          </button>
        </form>
      )}
    </div>
  );
}


// ─── Signed-in devices list ─────────────────────────────────────────────────
function DevicesList() {
  const [rows, setRows] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/auth/devices");
      setRows(r.data?.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (hint) => {
    if (!window.confirm("Sign this device out?")) return;
    try {
      await api.post("/auth/devices/revoke", { token_hint: hint });
      toast.success("Device signed out");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't sign out that device";
      toast.error(msg);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-stone-200 p-4" data-testid="devices-list">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold uppercase text-stone-500 flex items-center gap-1.5">
          <Monitor size={14} /> Signed-in devices
        </div>
        <button onClick={load} className="text-[11px] text-emerald-700 font-bold" data-testid="devices-refresh">
          Refresh
        </button>
      </div>
      {!rows && <div className="text-sm text-stone-400 py-4 text-center">Loading…</div>}
      {rows && rows.length === 0 && (
        <div className="text-sm text-stone-500 py-4 text-center">No active sessions.</div>
      )}
      <div className="space-y-2">
        {rows?.map(r => (
          <div
            key={r.token_hint}
            className={`flex items-center gap-3 rounded-xl border p-3 ${r.current ? "border-emerald-200 bg-emerald-50" : "border-stone-100"}`}
            data-testid={`device-row-${r.token_hint}`}
          >
            <div className="w-9 h-9 rounded-full bg-stone-100 grid place-items-center shrink-0">
              <Smartphone size={16} className="text-stone-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-stone-900">
                {r.current ? "This device" : "Signed-in device"}
                <span className="ml-2 text-[10px] font-mono text-stone-400">{r.token_hint}</span>
              </div>
              <div className="text-[11px] text-stone-500">
                {r.source || "web"} · created {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
              </div>
            </div>
            {!r.current && (
              <button
                onClick={() => revoke(r.token_hint)}
                className="p-2 rounded-full text-rose-600 hover:bg-rose-50"
                aria-label="Sign out remotely"
                data-testid={`device-revoke-${r.token_hint}`}
              >
                <LogOut size={15} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

